/** The player's timeline: waveform, thumbnail strip, buffered range, programme and in/out marks, scrubbing preview. */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { spriteTileAt, type SpriteLayout } from '@prism/contracts';
import { spriteStyle } from '../components/SpriteThumb';

interface Waveform { peaks: number[]; length: number }

const STRIP_H = 40;

export interface StripTile { left: number; width: number; index: number }

/**
 * The filmstrip's tiles (px on a strip `width` wide, each at most `slot` px: a tile's natural width at the strip's
 * height). A timed sprite: equal slots, each the frame at its middle. A sprite from shots (1.5.0, tileSeconds): each
 * tile drawn from its time, so a shot's first frame starts where the shot starts and lines up with the waveform; tiles
 * closer than half a slot to the one before are left out (no slivers), and a long stretch is split into equal pieces
 * of at most a slot, each the latest tile taken at or before it.
 */
export function filmstripTiles(layout: SpriteLayout, duration: number, width: number, slot: number): StripTile[] {
  if (!duration || width <= 0 || slot <= 0) return [];
  const times = layout.tileSeconds;
  if (!times || !times.length) {
    const n = Math.max(1, Math.floor(width / slot));
    return Array.from({ length: n }, (_, i) => ({ left: (i / n) * width, width: width / n, index: spriteTileAt(layout, ((i + 0.5) / n) * duration) }));
  }
  // Anchors: tiles at least half a slot apart (in a crowd of close cuts, the first is drawn); tile 0 starts the strip.
  // Shot starts first, so cuts line up; then the forced tiles where they have room between them.
  const minW = slot / 2;
  const n = Math.min(times.length, layout.count);
  const xOf = (i: number) => (times[i]! / duration) * width;
  const starts: Array<{ x: number; index: number }> = [{ x: 0, index: spriteTileAt(layout, 0) }];
  for (let i = 0; i < n; i++) {
    const x = xOf(i);
    if (!layout.forced?.[i] && x >= starts[starts.length - 1]!.x + minW && x < width) starts.push({ x, index: i });
  }
  const anchors: Array<{ x: number; index: number }> = [];
  let f = 0;
  starts.forEach((a, k) => {
    anchors.push(a);
    const to = k + 1 < starts.length ? starts[k + 1]!.x : width;
    for (; f < n; f++) {
      const x = xOf(f);
      if (x >= to) break;
      if (layout.forced?.[f] && x >= anchors[anchors.length - 1]!.x + minW && x <= to - minW) anchors.push({ x, index: f });
    }
  });
  const out: StripTile[] = [];
  anchors.forEach((a, n) => {
    const to = n + 1 < anchors.length ? anchors[n + 1]!.x : width;
    const gap = to - a.x;
    // A long gap: equal pieces of at most a slot (each at least half a slot), each the tile at or before its start.
    const k = Math.max(1, Math.ceil(gap / slot - 1e-9));
    for (let p = 0; p < k; p++) {
      const left = a.x + (gap * p) / k;
      out.push({ left, width: gap / k, index: p === 0 ? a.index : Math.max(a.index, spriteTileAt(layout, (left / width) * duration)) });
    }
  });
  return out;
}

export function Timeline({ duration, time, buffered, range, waveformUrl, waveformKey, spriteUrl, spriteLayout, tcAt, onSeek, marks, strip, children, testId = 'timeline' }: {
  duration: number; time: number; buffered: number; range: { a: number; b: number } | null;
  waveformUrl: string | null; waveformKey: string | null; spriteUrl: string | null; spriteLayout: SpriteLayout | null;
  tcAt: (s: number) => string; onSeek: (s: number) => void;
  marks: { in: number | null; out: number | null }; strip: boolean;
  /** Extra marks drawn on the bar (the sequence viewer's cuts and markers, 0.96.0). */
  children?: ReactNode;
  testId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [wave, setWave] = useState<Waveform | null>(null);
  const [width, setWidth] = useState(0);
  const dragging = useRef(false);

  useEffect(() => {
    if (!waveformUrl) return setWave(null);
    let cancelled = false;
    fetch(waveformUrl).then((r) => (r.ok ? r.json() : null)).then((d) => { if (!cancelled) setWave(d); }).catch(() => undefined);
    return () => { cancelled = true; };
    // Keyed by representation: token rotation in the URL does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waveformKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c || !width) return;
    const dpr = window.devicePixelRatio || 1;
    const h = c.clientHeight;
    c.width = width * dpr;
    c.height = h * dpr;
    const ctx = c.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, h);
    if (!wave || !wave.length) {
      ctx.fillStyle = 'rgba(255,255,255,.05)';
      for (let x = 0; x < width; x += 6) ctx.fillRect(x, h / 2 - 0.5, 3, 1);
      return;
    }
    let max = 1;
    for (const p of wave.peaks) max = Math.max(max, Math.abs(p));
    const mid = h / 2;
    const scale = (h / 2 - 3) / max;
    const played = duration ? (time / duration) * width : 0;
    for (let x = 0; x < width; x++) {
      const i0 = Math.floor((x / width) * wave.length);
      const i1 = Math.max(i0 + 1, Math.floor(((x + 1) / width) * wave.length));
      let lo = 0;
      let hi = 0;
      for (let i = i0; i < i1; i++) {
        lo = Math.min(lo, wave.peaks[i * 2] ?? 0);
        hi = Math.max(hi, wave.peaks[i * 2 + 1] ?? 0);
      }
      ctx.fillStyle = x < played ? 'rgba(165,151,255,.85)' : 'rgba(165,151,255,.38)';
      ctx.fillRect(x, mid - hi * scale, 1, Math.max(1, (hi - lo) * scale));
    }
  }, [wave, width, time, duration]);

  const toTime = (clientX: number) => {
    const r = ref.current!.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * duration;
  };
  const pct = (s: number) => `${duration ? (s / duration) * 100 : 0}%`;
  // 1.5.0: the tile taken at or before the time (a sprite from shots has its tiles' times; a timed one, its interval).
  const spriteIndex = hover != null && spriteLayout ? spriteTileAt(spriteLayout, hover) : 0;
  // A strip of thumbnails above the waveform (filmstripTiles).
  const showStrip = strip && Boolean(spriteUrl && spriteLayout && duration);
  const slot = showStrip ? (STRIP_H * spriteLayout!.tileWidth) / spriteLayout!.tileHeight : 0;
  const stripTiles = useMemo(() => (showStrip && width ? filmstripTiles(spriteLayout!, duration, width, slot) : []), [showStrip, spriteLayout, duration, width, slot]);

  return (
    <div
      className={`timeline ${showStrip ? 'with-strip' : ''}`}
      ref={ref}
      role="slider"
      tabIndex={0}
      aria-label="Playhead position"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(time)}
      aria-valuetext={tcAt(time)}
      onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); onSeek(toTime(e.clientX)); }}
      onPointerMove={(e) => { const t = toTime(e.clientX); setHover(t); if (dragging.current) onSeek(t); }}
      onPointerUp={() => { dragging.current = false; }}
      onPointerLeave={() => setHover(null)}
      data-testid={testId}
    >
      {showStrip && (
        <div className="filmstrip" data-testid="filmstrip" aria-hidden>
          {stripTiles.map((t, i) => (
            // A clip box at the tile's place; the frame inside keeps its natural width (cut on the right, never squeezed).
            <div key={i} className="tile-clip" data-tile={t.index} style={{ left: `${(t.left / width) * 100}%`, width: `${(t.width / width) * 100}%` }}>
              <div className="tile" style={{ ...spriteStyle(spriteUrl!, spriteLayout!, t.index), aspectRatio: 'auto', width: Math.max(t.width, slot) }} />
            </div>
          ))}
        </div>
      )}
      <canvas ref={canvasRef} />
      <div className="buffered" style={{ left: 0, width: pct(buffered) }} />
      {marks.in != null && marks.in > 0 && <div className="pgm-outside" style={{ left: 0, width: pct(marks.in) }} />}
      {marks.out != null && marks.out < duration && <div className="pgm-outside" style={{ left: pct(marks.out), right: 0 }} />}
      {marks.in != null && <div className="pgm-mark in" style={{ left: pct(marks.in) }} data-testid="pgm-in"><span>IN</span></div>}
      {marks.out != null && <div className="pgm-mark out" style={{ left: pct(marks.out) }} data-testid="pgm-out"><span>OUT</span></div>}
 {children}
      {range && <div className="range" style={{ left: pct(range.a), width: `calc(${pct(range.b)} - ${pct(range.a)})` }} />}
      {hover != null && <div className="hover-line" style={{ left: pct(hover) }} />}
      <div className="playhead" style={{ left: `calc(${pct(time)} - 1px)` }} />
      {hover != null && (
        <div className="scrub-preview" style={{ left: `clamp(90px, ${pct(hover)}, calc(100% - 90px))` }}>
          {spriteUrl && spriteLayout && <div className="frame" style={{ ...spriteStyle(spriteUrl, spriteLayout, spriteIndex), width: 176 }} data-testid="scrub-preview-frame" data-tile={spriteIndex} />}
          <span className="tc">{tcAt(hover)}</span>
        </div>
      )}
    </div>
  );
}
